import { type FC, type ReactNode, useCallback, useState } from 'react';

import classNames from 'classnames';

import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { EmptyState } from '@iconik-io/uad';

import { CUTTING_ROOM_HIDDEN_UI } from '../../components/cutting_room/cutting_room_config';
import { CuttingRoomIframe } from '../../components/cutting_room/cutting_room_iframe';
import DraggableItems, {
  type DraggableItemsModifierKeys,
} from '../../components/draggable_items/draggable_items';
import SearchListDragImage from '../../components/search_items/search_items_drag_image/search_items_list_drag_image';
import { CUTTING_ROOM_PROJECT_PANEL } from '../../constants/data';
import { NEW_CUTTING_ROOM_PROJECT_MODAL } from '../../constants/modals';
import { addItemsToProjectBin } from '../../ducks/cutting_room/thunks';
import { SEARCH_DRAG } from '../../ducks/dragged_items/constants';
import { openModal } from '../../ducks/modals_manager/slice';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';

import styles from './cutting_room_project.module.css';

const DROP_TARGET_ID = CUTTING_ROOM_PROJECT_PANEL;
const EMPTY_ITEMS: SearchItemEntity[] = [];
const NO_DROP_TARGETS: string[] = [];

const CuttingRoomProjectPanel: FC = () => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(
    (state) => state.cuttingRoom.projectId ?? undefined,
  );
  const pendingProject = useAppSelector(
    (state) => state.cuttingRoom.pendingProject,
  );
  const draggedItems = useAppSelector(
    (state) => state.draggedItemsReducer.items[SEARCH_DRAG] ?? EMPTY_ITEMS,
  );
  const dropTargetId = useAppSelector(
    (state) => state.draggedItemsReducer.targets[SEARCH_DRAG],
  );

  const [listDropTargetId, setListDropTargetId] = useState<
    string | undefined
  >();

  // A project being created stays out of view until it has loaded, whether or
  // not another one was open before.
  const hasProject = Boolean(projectId) && !pendingProject;

  const promptForProject = () => {
    dispatch(openModal({ id: NEW_CUTTING_ROOM_PROJECT_MODAL }));
  };

  const handleDrop = useCallback(
    (
      items: SearchItemEntity[],
      _targetId: string | null,
      _modifierKeys: DraggableItemsModifierKeys,
    ) => {
      if (items.length > 0) {
        dispatch(addItemsToProjectBin(items));
      }
    },
    [],
  );

  const isDragOver =
    dropTargetId === DROP_TARGET_ID || listDropTargetId === DROP_TARGET_ID;
  const hasDraggedItems = draggedItems.length > 0;

  let placeholder: ReactNode;
  if (pendingProject) {
    placeholder = (
      <EmptyState
        iconName="project"
        title="Creating project"
        description={`Setting up "${pendingProject.title}" in CuttingRoom…`}
      />
    );
  } else if (!hasProject) {
    placeholder = (
      <EmptyState
        iconName="project"
        title="No project open"
        description="Create a CuttingRoom project to start editing, or open an existing one from its context menu."
        actionButtonLabel="Create project"
        onAction={promptForProject}
      />
    );
  }

  return (
    <DraggableItems
      className={classNames(styles.root, {
        [styles.dragging]: hasDraggedItems,
      })}
      name={SEARCH_DRAG}
      dragImageComponent={SearchListDragImage}
      onDrop={handleDrop}
      selectedItems={EMPTY_ITEMS}
      allDropTargetIds={hasProject ? [DROP_TARGET_ID] : NO_DROP_TARGETS}
      setListDropTargetId={setListDropTargetId}
      copyOnly
    >
      <div data-id={DROP_TARGET_ID} className={styles.drop_target}>
        <div
          className={classNames(styles.drop_overlay, {
            [styles.drop_overlay_active]:
              hasProject && isDragOver && hasDraggedItems,
          })}
        >
          <span className={styles.drop_label}>Add to project bin</span>
        </div>
        {/*
          Kept mounted with no project: `createCuttingRoomProject` creates the
          project through this iframe's bridge, so it has to be signed in and
          ready by the time the prompt is confirmed.
        */}
        <CuttingRoomIframe
          view="project"
          projectId={projectId}
          hide={CUTTING_ROOM_HIDDEN_UI}
          placeholder={placeholder}
          isLoading={Boolean(pendingProject)}
        />
      </div>
    </DraggableItems>
  );
};

export default CuttingRoomProjectPanel;
